import { TreeNode } from "@/types/ModelTreeNode";
import { AppSettingType, CadModelCategory, Co2MaterialLibrary, Co2MaterialType } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useCo2MaterialLibraries } from "@api/co2MaterialLibraries";
import MultiGridToolbarSearchFilter from "@components/DataGrid/components/toolbar/MultiGridToolbarSearchFilter";
import { SplitPane, Splitter } from "@components/Splitter/Splitter";
import useAppStore from "@hooks/useAppStore";
import { useMitt } from "@hooks/useMitt";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { Tab, Tabs, Tooltip, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Grid } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { ElementMetrics } from "@stores/CompareMaterialsStore";
import { MaterialTabList, MaterialTabType } from "@/types/MaterialTabs";
import { getClientConfigKey, showMPATabs } from "@utils/clientConfigUtils";
import isGuid from "@utils/isGuid";
import { getCategoryFromKey } from "@utils/treeNodes";
import { observer } from "mobx-react";
import { createRef, forwardRef, RefObject, useEffect, useImperativeHandle, useRef } from "react";
import useState from "react-usestateref";
import useResizeObserver from "use-resize-observer";
import useCompareMaterialsStore from "../../hooks/useCompareMaterialsStore";
import MaterialLibrarySelect from "../DataGrid/components/toolbar/MaterialLibrarySelect";
import MaterialSelectCompare from "./MaterialSelectCompare";
import { MaterialSelectGrid } from "./MaterialSelectGrid";

export interface MaterialSelectDialogProps {
  showMetrics?: boolean;
  showCompare?: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (materialType: Co2MaterialType) => void;
}

const MaterialSelectDialog = observer(
  forwardRef((props: MaterialSelectDialogProps, ref: any) => {
    const appStore = useAppStore();
    const clientKey = getClientConfigKey(appStore.applicationSettings);

    const showTabs = showMPATabs(appStore.applicationSettings);
    const [tabIndex, setTabIndex] = useState(0);
    const activeType: MaterialTabType = showTabs ? MaterialTabList[tabIndex] : MaterialTabList[0];

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
      setTabIndex(newValue);
    };

    const compareMaterialsStore = useCompareMaterialsStore();
    const { emitter } = useMitt();
    const containerRef = useRef(null);
    const { height } = useResizeObserver({ ref: containerRef });
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedMaterialLibraries, setSelectedMaterialLibraries] = useState<Co2MaterialLibrary[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [compareSelectModel, setCompareSelectModel] = useState<any>({});
    const [isCompareOpen, setIsCompareOpen] = useState(false);
    const [collapse, setCollapse] = useState(true);
    const [defaultSizes, setDefaultSizes] = useState(appStore.materialSelectDialogSizes);
    const [materialSelectGridRefs, setMaterialSelectGridRefs] = useState<Array<RefObject<any>>>([]);
    const collapseSizes = [100, 0];
    const cadModelCategories = useCadModelCategories();
    const materialLibraries = useCo2MaterialLibraries();

    useEffect(() => {
      if (appStore.co2MaterialLibraryIds.length === 0) {
        const defaultMaterialLibraryId = materialLibraries.data.at(0)?.co2MaterialLibraryId;
        appStore.setMaterialLibraryIds(defaultMaterialLibraryId ? [defaultMaterialLibraryId] : []);
      }
      const newSelections = materialLibraries.data.filter((library: Co2MaterialLibrary) => appStore.co2MaterialLibraryIds.includes(library.co2MaterialLibraryId!));
      setMaterialSelectGridRefs(Array.from(newSelections, () => createRef()));
      setSelectedMaterialLibraries(newSelections);
    }, [materialLibraries.data]);

    const [formData, setFormData] = useState({
      selectedCategories: "",
      metrics: new ElementMetrics(),
      display: { area: { total: "" }, length: { total: "" }, volume: { total: "" }, quantity: { total: "" }, co2eTotal: "" },
    });
    const localStorageKey = `matSelDlg`;

    useEffect(() => {
      compareMaterialsStore.clear();
      const prevState = localStorage.getItem(localStorageKey);
      console.log(`${localStorageKey} state:`, prevState);
      if (prevState) {
        const state = JSON.parse(prevState);
        setSearchText(state.searchText);
      }
    }, []);

    useImperativeHandle(ref, () => ({
      setIsLoading: (value: boolean) => setIsLoading(value),
    }));

    // TODO: type the form data
    const [selectedMaterialType, setSelectedMaterialType] = useState<Co2MaterialType | null>(null);

    // TODO: if we have selected child nodes and partially selected categories
    // we need to compute the totals from the selected child nodes. That would require looking
    // at each of the appStore.selectedNodesKeys.
    useEffect(() => {
      const selectedModelCategories = { ...appStore.selectedModelCategories };

      const metrics = new ElementMetrics();

      // Get category totals
      metrics.length.total += Object.keys(selectedModelCategories).reduce((acc: number, key: string) => {
        return acc + selectedModelCategories[key].data.lengthTotal;
      }, 0);

      metrics.area.total += Object.keys(selectedModelCategories).reduce((acc: number, key: string) => {
        return acc + selectedModelCategories[key].data.areaTotal;
      }, 0);

      metrics.volume.total += Object.keys(selectedModelCategories).reduce((acc: number, key: string) => {
        return (
          acc +
          (selectedModelCategories[key].data.volumeTotal || 0) -
          (selectedModelCategories[key].data.rebarVolumeTotal || 0) /*- (selectedModelCategories[key].data.nonModVolumeTotal || 0)*/
        );
      }, 0);

      metrics.quantity.total += Object.keys(selectedModelCategories).reduce((acc: number, key: string) => {
        return acc + selectedModelCategories[key].data.quantityTotal;
      }, 0);

      metrics.co2eTotal += Object.keys(selectedModelCategories).reduce((acc: number, key: string) => {
        return acc + (selectedModelCategories[key].data.co2Totals?.totalCo2 || 0) - (selectedModelCategories[key].data.rebar?.co2Totals?.totalCo2 || 0) - (selectedModelCategories[key].data.nonMod?.co2Totals?.totalCo2 || 0);
      }, 0);

      // Get selected child nodes
      const selectedNodesKeys = Object.keys(appStore.selectedNodesKeys)
        .filter((key: string) => isGuid(key))
        .filter((key: string) => !Object.hasOwnProperty.call(appStore.selectedModelCategories, key));

      // Get totals from selected child nodes
      appStore.assignNodes?.forEach((node: TreeNode) => {
        const isCategoryChecked = appStore.selectedNodesKeys[node.data.cadModelCategoryId];
        if (!isCategoryChecked) {
          if (node.leaf) {
            if (selectedNodesKeys.includes((node.data.elementId || node.data.componentId) as string)) {
              metrics.length.total += node.data.lengthTotal;
              metrics.area.total += node.data.areaTotal;
              metrics.volume.total += node.data.volumeTotal - (node.data.rebarVolumeTotal || 0) /*- (node.data.nonModVolumeTotal || 0)*/;
              metrics.quantity.total += node.data.quantityTotal;
              metrics.co2eTotal += (node.data.co2Totals?.totalCo2 ?? 0) - (node.data.rebar?.co2Totals?.totalCo2 ?? 0) - (node.data.nonMod?.co2Totals?.totalCo2 ?? 0);

              const category = getCategoryFromKey(node.key as string, appStore.assignNodes as TreeNode[]);
              if (category) {
                if (!Object.hasOwnProperty.call(selectedModelCategories, category.key as string)) {
                  selectedModelCategories[category.key as string] = category;
                }
              }
            }
          }
        }
      });

      // Get the names of categories with selected nodes
      const selectedCategories = Object.entries(selectedModelCategories).reduce((acc: string[], [key, value]) => {
        console.log("key :", key);
        const label = cadModelCategories.data?.find((cmc: CadModelCategory) => cmc.cadModelCategoryId === value?.data?.cadModelCategoryId)?.label;
        if (label && !acc.includes(label)) {
          acc.push(label);
        }
        return acc;
      }, []);

      let display = {
        length: { total: i18n.t("number", { value: metrics.length.total, digits: AppSettingType.NumberFormat_EC_Digits }) },
        area: { total: i18n.t("number", { value: metrics.area.total, digits: AppSettingType.NumberFormat_EC_Digits }) },
        volume: { total: i18n.t("number", { value: metrics.volume.total, digits: AppSettingType.NumberFormat_EC_Digits }) },
        quantity: { total: i18n.t("number", { value: metrics.quantity.total, digits: AppSettingType.NumberFormat_EC_Digits }) },
        co2eTotal: i18n.t("number", { value: metrics.co2eTotal, digits: AppSettingType.NumberFormat_EC_Digits }),
      };

      setSelectedCategories(selectedCategories.sort());
      setFormData({
        selectedCategories: selectedCategories.join(", "),
        metrics,
        display,
      });

      compareMaterialsStore.setElementMetrics(metrics);
    }, [appStore.selectedModelCategories]);

    const handleClose = async () => {
      // TODO: why both of these?
      emitter.emit("close");
      if (props.onClose) {
        props.onClose();
      }
    };

    const handleAssign = () => {
      // If the user has not made a material selection then do not submit the form
      if (selectedMaterialType) {
        setIsLoading(true);
        props.onSubmit(selectedMaterialType);
      } else {
        // TODO: show error message
      }
    };

    const handleMaterialLibrarySelectChange = async (_event: any, newSelections: Co2MaterialLibrary[]) => {
      {
        // Cleanup the compare materials store
        const removeSelections = selectedMaterialLibraries.filter((selection: Co2MaterialLibrary) => !newSelections.includes(selection));
        removeSelections.forEach((selection: Co2MaterialLibrary) => compareMaterialsStore.removeCo2MaterialLibrary(selection.co2MaterialLibraryId!));
      }

      {
        // Cleanup the compare material select model
        const newCompareSelectModel = compareMaterialsStore.co2MaterialTypes.reduce((acc: any, co2MaterialType: Co2MaterialType) => {
          const id = co2MaterialType.co2MaterialTypeId || co2MaterialType.externalId;
          acc[id!] = true;
          return acc;
        }, {});
        setCompareSelectModel(newCompareSelectModel);
      }

      setMaterialSelectGridRefs(Array.from(newSelections, () => createRef()));
      setSelectedMaterialLibraries(newSelections);
      appStore.setMaterialLibraryIds(newSelections.map((selection: Co2MaterialLibrary) => selection.co2MaterialLibraryId!));
    };

    const handleRowSelectModelChange = (co2MaterialLibrary: Co2MaterialLibrary, co2MaterialType: Co2MaterialType) => {
      emitter.emit("resetSelection", { co2MaterialLibrary, co2MaterialType });
      setSelectedMaterialType(co2MaterialType);
    };

    const handleSearch = (searchText: string) => {
      localStorage.setItem(localStorageKey, JSON.stringify({ searchText }));
      setSearchText(searchText);
    };

    // If the calculated grid height is less than 400px then set the grid height to the full height
    // and let scrolling occur on the dialog
    let gridHeight = (height || 0) / selectedMaterialLibraries.length - 8;
    if (gridHeight < 300) {
      gridHeight = (height || 0) - 8;
    }

    const handleToggleCompareSelectModel = (id: string, checked: boolean) => {
      setCompareSelectModel((prevValue: any) => {
        return { ...prevValue, [id]: !checked };
      });
    };

    const handleShowCompare = () => {
      console.log("Show compare: ", !isCompareOpen);
      setCollapse(isCompareOpen);
      setIsCompareOpen(!isCompareOpen);
    };

    const onDragEnd = (event: any) => {
      console.log(event);
      setDefaultSizes(event);
      appStore.setMaterialSelectDialogSizes(event);
    };

    const handleMaterialSelect = (co2MaterialType: Co2MaterialType | null | undefined) => {
      materialSelectGridRefs.forEach((ref: RefObject<any>) => {
        ref.current?.selectMaterial(co2MaterialType);
      });
    };

    return (
      <Dialog
        fullWidth={true}
        maxWidth={false}
        open={props.open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            height: "90%",
            width: "100%",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {i18n.t("assign-material-dialog.title")}
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ height: "100% " }}>
          {/* <DialogContentText id="alert-dialog-description"> */}
          <Box component="form" sx={{ height: "100%", "& .gutter": { display: collapse ? "none" : "block" } }}>
            <Splitter
              onDragEnd={(e: any) => onDragEnd(e)}
              minSize={0}
              sizes={collapse ? collapseSizes : defaultSizes}
              style={{ display: "flex", flexDirection: "row", flexGrow: 1, height: "100%" }}
            >
              <SplitPane style={{ minWidth: 0, height: "100%" }}>
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 1, overflow: "auto" }}>
                  {!!props.showMetrics && (
                    <Grid container spacing={2} sx={{ margin: 0 }}>
                      <Grid size={{ xs: 3, md: 4 }} sx={{ "& .MuiFormControl-root": { margin: "8px 0" } }}>
                        <Tooltip
                          title={
                            <>
                              {selectedCategories.map((category: string) => (
                                <Typography key={category}>{category}</Typography>
                              ))}
                            </>
                          }
                        >
                          <TextField
                            label={i18n.t("assign-material-dialog.categories-input.title")}
                            name="selectedCategories"
                            value={formData.selectedCategories}
                            margin="none"
                            variant="outlined"
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },

                              inputLabel: {
                                shrink: true,
                              },
                            }}
                          />
                        </Tooltip>
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }} sx={{ "& .MuiFormControl-root": { margin: "8px 0" } }}>
                        <TextField
                          fullWidth={true}
                          label={i18n.t("assign-material-dialog.length-input.title")}
                          name="length"
                          type="length"
                          value={formData.display.length.total}
                          variant="outlined"
                          slotProps={{
                            input: {
                              readOnly: true,
                            },

                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }} sx={{ "& .MuiFormControl-root": { margin: "8px 0" } }}>
                        <TextField
                          fullWidth={true}
                          label={i18n.t("assign-material-dialog.area-input.title")}
                          name="area"
                          type="area"
                          value={formData.display.area.total}
                          variant="outlined"
                          slotProps={{
                            input: {
                              readOnly: true,
                            },

                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }} sx={{ "& .MuiFormControl-root": { margin: "8px 0" } }}>
                        <TextField
                          fullWidth={true}
                          label={i18n.t("assign-material-dialog.volume-input.title")}
                          name="volume"
                          type="volume"
                          value={formData.display.volume.total}
                          variant="outlined"
                          slotProps={{
                            input: {
                              readOnly: true,
                            },

                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }} sx={{ "& .MuiFormControl-root": { margin: "8px 0" } }}>
                        <TextField
                          fullWidth={true}
                          label={i18n.t("assign-material-dialog.co2e-input.title")}
                          name="co2e"
                          type="co23"
                          value={formData.display.co2eTotal}
                          variant="outlined"
                          slotProps={{
                            input: {
                              readOnly: true,
                            },

                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}
                  <Grid container spacing={2} sx={{ margin: 0, marginTop: 1, alignItems: "end" }}>
                    <Grid size={6} sx={{ paddingTop: "!important", "& .MuiFormControl-root": { margin: "8px 0" } }}>
                      <MultiGridToolbarSearchFilter onChange={handleSearch} value={searchText} />
                    </Grid>
                    <Grid size={6} sx={{ display: "flex", justifyContent: "end", paddingTop: "!important" }}>
                      <MaterialLibrarySelect options={materialLibraries?.data} value={selectedMaterialLibraries} onChange={handleMaterialLibrarySelectChange} />
                    </Grid>
                  </Grid>

                  {showTabs && (
                    <Box sx={{ borderBottom: 1, borderColor: "divider", pl: 2 }}>
                      <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        textColor="primary"
                        indicatorColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          minHeight: 32,
                          height: 32,
                          ".MuiTab-root": {
                            minHeight: 32,
                            height: 32,
                            paddingX: 2,
                            fontSize: 14,
                            textTransform: "none",
                          },
                        }}
                      >
                        {MaterialTabList.map((key) => (
                          <Tab key={key} label={i18n.t(`materials-page.tabs.${key}`)} sx={{ fontWeight: activeType === key ? 600 : 400 }} />
                        ))}
                      </Tabs>
                    </Box>
                  )}
                  <Box ref={containerRef} sx={{ display: "flex", flexDirection: "column", flexGrow: 1, flexShrink: 1, overflowY: "auto" }}>
                    {selectedMaterialLibraries.map((library: Co2MaterialLibrary, index: number) => (
                      <Box
                        sx={{
                          width: "100%",
                          height: `${gridHeight}px`,
                          marginBottom: 1,
                        }}
                        key={library.co2MaterialLibraryId}
                      >
                        <MaterialSelectGrid
                          ref={materialSelectGridRefs[index]}
                          key={library.co2MaterialLibraryId}
                          co2MaterialLibrary={library}
                          searchText={searchText}
                          metrics={formData.metrics}
                          compare={props.showCompare}
                          compareSelectModel={compareSelectModel}
                          onRowSelectModelChange={handleRowSelectModelChange}
                          onCompareSelectModelChange={handleToggleCompareSelectModel}
                          onShowCompare={handleShowCompare}
                          tabType={activeType}
                          showTabs={showTabs}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </SplitPane>
              <SplitPane style={{ minWidth: 0, height: "100%" }}>
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 1, overflow: "auto" }}>
                  <MaterialSelectCompare onMaterialSelect={handleMaterialSelect} />
                </Box>
              </SplitPane>
            </Splitter>
          </Box>
          {/* </DialogContentText> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{i18n.t("assign-material-dialog.close-button.title")}</Button>
          <Button disabled={!selectedMaterialType} loading={isLoading} variant="contained" onClick={handleAssign} autoFocus>
            {i18n.t("assign-material-dialog.assign-button.title")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }),
);

export default MaterialSelectDialog;
